import React, { useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";

// region Libraries
import * as dateFns from "date-fns";
import { Checkbox, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import {
  CanConsumptionMessages, DataTableMessages, FilterMessages, VehiclesCanHistoryMessages, VehicleTypesMessages
} from "@shared/languages/interfaces";
// endregion Languages
// region Interfaces
import { AnalyticVehicleCanHistory, SyntheticVehicleCanHistory, VehicleCanHistory } from "@shared/interfaces/vehicles-can-history.interface";
// endregion Interfaces
// region Entities
// endregion Entities
// region Utils
import utils from "@utils/useful-functions";
import { formatOdometer } from "@shared/utils/useful-functions";
import { sortByNatural, sortByValue } from "@utils/data-table-sorting";
import { TLanguages } from "@shared/languages/languageData";
// endregion Utils
// region Components
import Header from "@components/Header";
import NewDataTable, { DataTableActions, DataTableButtons, DataTableColumns } from "@molecules/NewDataTable";
import QueryFilterVehicleCanHistory from "@organisms/QueryFilterVehicleCanHistory";
import AlertsModal from "@organisms/AlertsModal";
import LimitedChipsField from "@atoms/LimitedChipsField";
// endregion Components
// region Styles
import { useSelector } from "react-redux";
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import { Container } from "./styles";
// endregion Styles

interface VehiclesCanHistoryProps {
  /** Rendered inside the CAN consumption tabs, which already draw the page header */
  embedded?: boolean;
}

const VehiclesCanHistory: React.FC<VehiclesCanHistoryProps> = ({ embedded = false }) => {

  /* region States */
  const [vehicleCanHistoryData, setVehicleCanHistoryData] = useState<VehicleCanHistory>(
    { analyticData: [], syntheticData: [] }
  );

  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);

  // Used to create export file name
  const [startDate, setStartDate] = useState<string>(
    dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00")
  );
  const [finishDate, setFinishDate] = useState<string>(dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59"));

  // AlertsModal state
  const [openAlertsModal, setOpenAlertsModal] = useState(false);
  const [selectedAlertIds, setSelectedAlertIds] = useState<string[]>([]);
  const [alertsModalTitle, setAlertsModalTitle] = useState<string>("");

  // In-memory filter states for the equipment table
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<string[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);

  /* endregion States */
  /* region Hooks */
  const { screen } = useSelector((screen) => screen);
  const { t, i18n } = useTranslation();
  /* endregion Hooks */
  /* region Derived filter data */
  const vehicleTypeOptions = useMemo(() => {
    const uniqueIds = new Set<string>();

    for (const row of vehicleCanHistoryData.analyticData) {
      uniqueIds.add(row.vehicleTypeId);
    }

    return Array.from(uniqueIds).sort((a, b) => {
      const labelA = t(VehicleTypesMessages[`${a}-plural`]) ?? a;
      const labelB = t(VehicleTypesMessages[`${b}-plural`]) ?? b;

      return labelA.localeCompare(labelB);
    });
  }, [vehicleCanHistoryData.analyticData, t]);

  const vehicleOptions = useMemo(() => {
    let data = vehicleCanHistoryData.analyticData;

    if (selectedVehicleTypes.length > 0) {
      const typeIds = new Set(selectedVehicleTypes);

      data = data.filter((row) => typeIds.has(row.vehicleTypeId));
    }

    const seen = new Set<string>();

    return data
      .filter((row) => {
        if (seen.has(row.vehicleName)) return false;
        seen.add(row.vehicleName);

        return true;
      })
      .sort((a, b) =>
        (t(VehicleTypesMessages[`${a.vehicleTypeId}-plural`]) ?? a.vehicleTypeId)
          .localeCompare(t(VehicleTypesMessages[`${b.vehicleTypeId}-plural`]) ?? b.vehicleTypeId)
        || a.vehicleName.localeCompare(b.vehicleName)
      );
  }, [vehicleCanHistoryData.analyticData, selectedVehicleTypes, t]);

  const vehicleOptionsByName = useMemo(() => {
    const map = new Map<string, AnalyticVehicleCanHistory>();

    for (const v of vehicleOptions) {
      map.set(v.vehicleName, v);
    }

    return map;
  }, [vehicleOptions]);

  const filteredAnalyticData = useMemo(() => {
    let data = vehicleCanHistoryData.analyticData;

    if (selectedVehicleTypes.length > 0) {
      const typeIds = new Set(selectedVehicleTypes);

      data = data.filter((row) => typeIds.has(row.vehicleTypeId));
    }

    if (selectedVehicles.length > 0) {
      const vehicleNames = new Set(selectedVehicles);

      data = data.filter((row) => vehicleNames.has(row.vehicleName));
    }

    return data;
  }, [vehicleCanHistoryData.analyticData, selectedVehicleTypes, selectedVehicles]);

  /* endregion Derived filter data */
  /* region Constants */
  const decimalSeparator: "." | "," = i18n.language === "en-US" ? "." : ",";

  // A consumption rate is null whenever it has no basis of calculation (no fuel reported, no engine
  // hours, no volume, not a concrete mixer). It must render as a dash: a "0" would be read as "this
  // vehicle consumed nothing".
  // The volume carried follows the same rule: no travel with a usable volume means an empty cell,
  // never a zero — a zero here would read as "this vehicle carried nothing".
  const formatVolume = (value: number | null): string => (
    value !== null && Number.isFinite(value)
      ? utils.formatNumberToLocaleString(value, i18n.language as TLanguages, 1)
      : "—"
  );

  const formatConsumptionRate = (value: number | null): string => (
    value !== null && Number.isFinite(value)
      ? utils.formatNumberToLocaleString(value, i18n.language as TLanguages, 2)
      : "—"
  );

  // General DataTable
  const generalDataTableSettings: DataTables.Settings = {
    order: [],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const generalDataTableActions: DataTableActions<SyntheticVehicleCanHistory>[] = [
    {
      ref: ".fueling-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.fuelingAlertIds, t(VehiclesCanHistoryMessages.fuelingAlerts))
    },
    {
      ref: ".sudden-drop-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.suddenDropAlertIds, t(VehiclesCanHistoryMessages.suddenDropAlerts))
    },
    {
      ref: ".speed-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.speedAlertIds, t(VehiclesCanHistoryMessages.speedingAlerts))
    },
    {
      ref: ".high-temperature-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.highTemperatureAlertIds, t(VehiclesCanHistoryMessages.highTemperatureAlerts))
    }
  ];
  const generalDataTableColumns: DataTableColumns[] = [
    { // Tipo de Veículo
      title: t(VehiclesCanHistoryMessages.vehicleType),
      data: (data: SyntheticVehicleCanHistory) => data.vehicleType,
      filterable: false
    },
    { // Quant. Veículos
      title: t(VehiclesCanHistoryMessages.vehicles),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.vehiclesCount, (row) => String(row.vehiclesCount)),
      filterable: false
    },
    { // Quant. Viagens
      title: t(VehiclesCanHistoryMessages.travels),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.travelsCount, (row) => String(row.travelsCount)),
      filterable: false
    },
    { // Volume Transportado (m³)
      title: t(CanConsumptionMessages.volumeCarriedColumn),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.volumeM3, (row) => formatVolume(row.volumeM3)),
      filterable: false
    },
    { // Total Distância Percorrida (km)
      title: t(VehiclesCanHistoryMessages.kmTraveled),
      data: sortByValue<SyntheticVehicleCanHistory>(
        (row) => row.distance,
        (row) => (row.distance !== null ? formatOdometer(row.distance, false, decimalSeparator, 2) : "")
      ),
      filterable: false
    },
    { // Consumo Total Combustível (L)
      title: t(VehiclesCanHistoryMessages.fuelConsumption),
      data: sortByValue<SyntheticVehicleCanHistory>(
        (row) => row.fuelConsumptionInLiters,
        (row) => utils.formatNumberToLocaleString(row.fuelConsumptionInLiters, i18n.language as TLanguages, 2)
      ),
      filterable: false
    },
    { // Consumo Marcha Lenta (L)
      title: t(VehiclesCanHistoryMessages.idleFuelConsumption),
      data: sortByValue<SyntheticVehicleCanHistory>(
        (row) => row.idleFuelConsumptionInLiters,
        (row) => utils.formatNumberToLocaleString(row.idleFuelConsumptionInLiters, i18n.language as TLanguages, 2)
      ),
      filterable: false
    },
    { // Consumo Médio em Movimento (km/L)
      title: t(VehiclesCanHistoryMessages.avgFuelConsumption),
      data: sortByValue<SyntheticVehicleCanHistory>(
        (row) => row.avgFuelConsumption,
        (row) => (row.avgFuelConsumption !== null
          ? utils.formatNumberToLocaleString(row.avgFuelConsumption, i18n.language as TLanguages, 2)
          : "")
      ),
      filterable: false
    },
    { // Consumo por Hora Motor (L/h)
      title: t(VehiclesCanHistoryMessages.litersPerHour),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.litersPerHour, (row) => formatConsumptionRate(row.litersPerHour)),
      filterable: false
    },
    { // Consumo por m³ (L/m³)
      title: t(VehiclesCanHistoryMessages.litersPerM3),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.litersPerM3, (row) => formatConsumptionRate(row.litersPerM3)),
      filterable: false
    },
    { // Consumo por Viagem (L)
      title: t(VehiclesCanHistoryMessages.litersPerTravel),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.litersPerTravel, (row) => formatConsumptionRate(row.litersPerTravel)),
      filterable: false
    },
    { // Consumo Marcha Lenta por Hora (L/h)
      title: t(VehiclesCanHistoryMessages.idleLitersPerHour),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.idleLitersPerHour, (row) => formatConsumptionRate(row.idleLitersPerHour)),
      filterable: false
    },
    { // Tempo Motor Ligado (h:mm)
      title: t(VehiclesCanHistoryMessages.engineRuntime),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.engineHoursInMinutes, (row) => row.engineHours),
      filterable: false
    },
    { // Marcha Lenta (h:mm)
      title: t(VehiclesCanHistoryMessages.idleEngineHours),
      data: sortByValue<SyntheticVehicleCanHistory>((row) => row.idleEngineHoursInMinutes, (row) => row.idleEngineHours),
      filterable: false
    },
    { // Alerta Variação Combustível (+)
      title: t(VehiclesCanHistoryMessages.fuelingAlerts),
      data: (data: SyntheticVehicleCanHistory) => {
        if (data.fuelingAlertIds.length === 0) return data.fuelingAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link fueling-alerts">{data.fuelingAlertsCount}</span>
        );
      },
      filterable: false
    },
    { // Alerta Variação Combustível (-)
      title: t(VehiclesCanHistoryMessages.suddenDropAlerts),
      data: (data: SyntheticVehicleCanHistory) => {
        if (data.suddenDropAlertIds.length === 0) return data.suddenDropAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link sudden-drop-alerts">{data.suddenDropAlertsCount}</span>
        );
      },
      filterable: false
    },
    { // Alerta Velocidade Excedida
      title: t(VehiclesCanHistoryMessages.speedingAlerts),
      data: (data: SyntheticVehicleCanHistory) => {
        if (data.speedAlertIds.length === 0) return data.speedAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link speed-alerts">{data.speedAlertsCount}</span>
        );
      },
      filterable: false
    },
    { // Alerta Temperatura Elevada
      title: t(VehiclesCanHistoryMessages.highTemperatureAlerts),
      data: (data: SyntheticVehicleCanHistory) => {
        if (data.highTemperatureAlertIds.length === 0) return data.highTemperatureAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link high-temperature-alerts">{data.highTemperatureAlertsCount}</span>
        );
      },
      filterable: false
    }
  ];

  // Equipment DataTable
  const equipmentDataTableSettings: DataTables.Settings = {
    order: [[0, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const equipmentDataTableActions: DataTableActions<AnalyticVehicleCanHistory>[] = [
    {
      ref: ".fueling-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.fuelingAlertIds, t(VehiclesCanHistoryMessages.fuelingAlerts))
    },
    {
      ref: ".sudden-drop-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.suddenDropAlertIds, t(VehiclesCanHistoryMessages.suddenDropAlerts))
    },
    {
      ref: ".speed-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.speedAlertIds, t(VehiclesCanHistoryMessages.speedingAlerts))
    },
    {
      ref: ".high-temperature-alerts",
      callback: (rowData) => handleOpenAlertsModal(rowData.highTemperatureAlertIds, t(VehiclesCanHistoryMessages.highTemperatureAlerts))
    }
  ];
  const equipmentDataTableColumns: DataTableColumns[] = [
    { // Veículo
      title: t(VehiclesCanHistoryMessages.equipment),
      // The table opens ordered by this column, and a fleet numbered by hand reads "1, 10, 100, 11, 2"
      // whenever the identifier is compared as text.
      data: sortByNatural<AnalyticVehicleCanHistory>((row) => row.vehicleName, (row) => row.vehicleName),
      defaultContent: "",
      filterable: true,
      propertyName: "vehicleName"
    },
    { // Placa
      title: t(VehiclesCanHistoryMessages.licensePlate),
      data: (data: AnalyticVehicleCanHistory) => data.licensePlate,
      filterable: true,
      propertyName: "licensePlate"
    },
    { // Quant. Viagens
      title: t(VehiclesCanHistoryMessages.travels),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.travelsCount, (row) => String(row.travelsCount)),
      filterable: true,
      propertyName: "travelsCount"
    },
    { // Volume Transportado (m³)
      title: t(CanConsumptionMessages.volumeCarriedColumn),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.volumeM3, (row) => formatVolume(row.volumeM3)),
      filterable: true,
      propertyName: "volumeM3"
    },
    { // Total Distância Percorrida (km)
      title: t(VehiclesCanHistoryMessages.kmTraveled),
      data: sortByValue<AnalyticVehicleCanHistory>(
        (row) => row.distance,
        (row) => (row.distance !== null ? formatOdometer(row.distance, false, decimalSeparator, 2) : "")
      ),
      filterable: true,
      propertyName: "distance"
    },
    { // Consumo Total Combustível (L)
      title: t(VehiclesCanHistoryMessages.fuelConsumption),
      data: sortByValue<AnalyticVehicleCanHistory>(
        (row) => row.fuelConsumptionInLiters,
        (row) => utils.formatNumberToLocaleString(row.fuelConsumptionInLiters, i18n.language as TLanguages, 2)
      ),
      filterable: true,
      propertyName: "fuelConsumptionInLiters"
    },
    { // Consumo Marcha Lenta (L)
      title: t(VehiclesCanHistoryMessages.idleFuelConsumption),
      data: sortByValue<AnalyticVehicleCanHistory>(
        (row) => row.idleFuelConsumptionInLiters,
        (row) => utils.formatNumberToLocaleString(row.idleFuelConsumptionInLiters, i18n.language as TLanguages, 2)
      ),
      filterable: true,
      propertyName: "idleFuelConsumptionInLiters"
    },
    { // Consumo Médio em Movimento (km/L)
      title: t(VehiclesCanHistoryMessages.avgFuelConsumption),
      data: sortByValue<AnalyticVehicleCanHistory>(
        (row) => row.avgFuelConsumption,
        (row) => (row.avgFuelConsumption !== null
          ? utils.formatNumberToLocaleString(row.avgFuelConsumption, i18n.language as TLanguages, 2)
          : "")
      ),
      filterable: true,
      propertyName: "avgFuelConsumption"
    },
    { // Consumo por Hora Motor (L/h)
      title: t(VehiclesCanHistoryMessages.litersPerHour),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.litersPerHour, (row) => formatConsumptionRate(row.litersPerHour)),
      filterable: true,
      propertyName: "litersPerHour"
    },
    { // Consumo por m³ (L/m³)
      title: t(VehiclesCanHistoryMessages.litersPerM3),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.litersPerM3, (row) => formatConsumptionRate(row.litersPerM3)),
      filterable: true,
      propertyName: "litersPerM3"
    },
    { // Consumo por Viagem (L)
      title: t(VehiclesCanHistoryMessages.litersPerTravel),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.litersPerTravel, (row) => formatConsumptionRate(row.litersPerTravel)),
      filterable: true,
      propertyName: "litersPerTravel"
    },
    { // Consumo Marcha Lenta por Hora (L/h)
      title: t(VehiclesCanHistoryMessages.idleLitersPerHour),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.idleLitersPerHour, (row) => formatConsumptionRate(row.idleLitersPerHour)),
      filterable: true,
      propertyName: "idleLitersPerHour"
    },
    { // Tempo Motor Ligado (h:mm)
      title: t(VehiclesCanHistoryMessages.engineRuntime),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.engineHoursInMinutes, (row) => row.engineHours),
      filterable: true,
      propertyName: "engineHoursInMinutes"
    },
    { // Marcha Lenta (h:mm)
      title: t(VehiclesCanHistoryMessages.idleEngineHours),
      data: sortByValue<AnalyticVehicleCanHistory>((row) => row.idleEngineHoursInMinutes, (row) => row.idleEngineHours),
      filterable: true,
      propertyName: "idleEngineHoursInMinutes"
    },
    { // Alerta Variação Combustível (+)
      title: t(VehiclesCanHistoryMessages.fuelingAlerts),
      data: (data: AnalyticVehicleCanHistory) => {
        if (data.fuelingAlertIds.length === 0) return data.fuelingAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link fueling-alerts">{data.fuelingAlertsCount}</span>
        );
      },
      filterable: true,
      propertyName: "fuelingAlertsCount"
    },
    { // Alerta Variação Combustível (-)
      title: t(VehiclesCanHistoryMessages.suddenDropAlerts),
      data: (data: AnalyticVehicleCanHistory) => {
        if (data.suddenDropAlertIds.length === 0) return data.suddenDropAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link sudden-drop-alerts">{data.suddenDropAlertsCount}</span>
        );
      },
      filterable: true,
      propertyName: "suddenDropAlertsCount"
    },
    { // Alerta Velocidade Excedida
      title: t(VehiclesCanHistoryMessages.speedingAlerts),
      data: (data: AnalyticVehicleCanHistory) => {
        if (data.speedAlertIds.length === 0) return data.speedAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link speed-alerts">{data.speedAlertsCount}</span>
        );
      },
      filterable: true,
      propertyName: "speedAlertsCount"
    },
    { // Alerta Temperatura Elevada
      title: t(VehiclesCanHistoryMessages.highTemperatureAlerts),
      data: (data: AnalyticVehicleCanHistory) => {
        if (data.highTemperatureAlertIds.length === 0) return data.highTemperatureAlertsCount;

        return ReactDOMServer.renderToString(
          <span className="alert-link high-temperature-alerts">{data.highTemperatureAlertsCount}</span>
        );
      },
      filterable: true,
      propertyName: "highTemperatureAlertsCount"
    }
  ];
  const equipmentDataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_equipamentos_can_${startDate}__${finishDate}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    }
  ];
  /* endregion Constants */

  /* region Functions */
  const handleApplyFilter = (
    numberOfFilteredOptions: number,
    returnedData: VehicleCanHistory,
    startDate: string,
    finishDate: string
  ) => {
    setNumberOfFilteredOptions(numberOfFilteredOptions);

    setVehicleCanHistoryData(returnedData);

    setStartDate(startDate);
    setFinishDate(finishDate);

    // Reset in-memory filters when new backend data arrives
    setSelectedVehicleTypes([]);
    setSelectedVehicles([]);
  };

  const handleVehicleTypeFilterChange = (
    _event: React.ChangeEvent<{}>,
    value: string[]
  ) => {
    setSelectedVehicleTypes(value);

    // Clear vehicle selections that no longer match the selected types
    if (value.length > 0) {
      const typeIds = new Set(value);
      const vehicleTypeMap = new Map<string, string>();

      for (const row of vehicleCanHistoryData.analyticData) {
        vehicleTypeMap.set(row.vehicleName, row.vehicleTypeId);
      }

      setSelectedVehicles((prev) => prev.filter((name) => typeIds.has(vehicleTypeMap.get(name) ?? "")));
    }
  };

  const handleOpenAlertsModal = (alertIds: string[], title: string) => {
    if (alertIds.length === 0) return;

    setSelectedAlertIds(alertIds);
    setAlertsModalTitle(title);
    setOpenAlertsModal(true);
  };

  const handleCloseAlertsModal = () => {
    setOpenAlertsModal(false);
    setSelectedAlertIds([]);
    setAlertsModalTitle("");
  };
  /* endregion Functions */

  return (
    <>
      {!embedded && <Header title={t(VehiclesCanHistoryMessages.title)} />}
      <Container platform={screen.platform as ScreenPlatform} className="page" embedded={embedded}>
        <div className="aggregated-data">
          <h2 className="dataTable-title">{t(VehiclesCanHistoryMessages.generalHistory)}</h2>
          <NewDataTable
            data={vehicleCanHistoryData.syntheticData}
            columns={generalDataTableColumns}
            settings={generalDataTableSettings}
            actions={generalDataTableActions}
            showPaging={false}
            showInfo={false}
            filters
            onClickFilterButton={() => setOpenFilterFields(true)}
            numberOfFilteredOptions={numberOfFilteredOptions}
          />
        </div>
        <div className="equipment-data">
          <div className="equipment-header">
            <h2 className="dataTable-title">{t(VehiclesCanHistoryMessages.historyByEquipment)}</h2>
            <div className="equipment-filters">
              <div className="equipment-filter-item">
                <Autocomplete
                  multiple
                  fullWidth
                  disableCloseOnSelect
                  options={vehicleTypeOptions}
                  getOptionSelected={(option, value) => option === value}
                  getOptionLabel={(option) => t(VehicleTypesMessages[`${option}-plural`]) ?? option}
                  value={selectedVehicleTypes}
                  onChange={handleVehicleTypeFilterChange}
                  renderTags={(value, getTagProps) => (
                    <LimitedChipsField
                      data={value}
                      limit={1}
                      getTagProps={getTagProps}
                      labelsFormat={(id: string) => t(VehicleTypesMessages[`${id}-plural`]) ?? id}
                      keysFormat={(id: string) => id}
                    />
                  )}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        checked={selected}
                      />
                      {t(VehicleTypesMessages[`${option}-plural`]) ?? option}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t(FilterMessages.optionVehicleTypes)}
                      size="small"
                    />
                  )}
                />
              </div>
              <div className="equipment-filter-item">
                <Autocomplete
                  multiple
                  fullWidth
                  disableCloseOnSelect
                  options={vehicleOptions.map((v) => v.vehicleName)}
                  getOptionSelected={(option, value) => option === value}
                  getOptionLabel={(option) => {
                    const vehicle = vehicleOptionsByName.get(option);

                    return vehicle ? `${vehicle.vehicleName} - ${vehicle.licensePlate}` : option;
                  }}
                  groupBy={(option) => {
                    const vehicle = vehicleOptionsByName.get(option);

                    return vehicle
                      ? (t(VehicleTypesMessages[`${vehicle.vehicleTypeId}-plural`]) ?? vehicle.vehicleTypeId)
                      : "";
                  }}
                  value={selectedVehicles}
                  onChange={(_event, value) => setSelectedVehicles(value)}
                  renderTags={(value, getTagProps) => (
                    <LimitedChipsField
                      data={value}
                      limit={1}
                      getTagProps={getTagProps}
                      labelsFormat={(name: string) => {
                        const vehicle = vehicleOptionsByName.get(name);

                        return vehicle ? `${vehicle.vehicleName} - ${vehicle.licensePlate}` : name;
                      }}
                      keysFormat={(name: string) => name}
                    />
                  )}
                  renderOption={(option, { selected }) => {
                    const vehicle = vehicleOptionsByName.get(option);

                    return (
                      <>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={selected}
                        />
                        {vehicle ? `${vehicle.vehicleName} - ${vehicle.licensePlate}` : option}
                      </>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t(FilterMessages.optionVehicles)}
                      size="small"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <NewDataTable
            title={t(VehiclesCanHistoryMessages.title)}
            data={filteredAnalyticData}
            columns={equipmentDataTableColumns}
            settings={equipmentDataTableSettings}
            actions={equipmentDataTableActions}
            buttons={equipmentDataTableButtons}
          />
        </div>
        <QueryFilterVehicleCanHistory
          open={openFilterFields}
          onClose={() => setOpenFilterFields(false)}
          onFilter={handleApplyFilter}
        />
      </Container>
      <AlertsModal
        open={openAlertsModal}
        onClose={handleCloseAlertsModal}
        alertIds={selectedAlertIds}
        title={alertsModalTitle}
      />
    </>
  );
};

export default VehiclesCanHistory;
